import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  buildPerformanceTableRows,
  currentUtcMonth,
  formatMonthYearUtcLabel,
  formatValue,
  monthKeyUtc,
  periodOrder,
  tableMonthBoundsFromDaily,
  utcMonthFromKey,
} from "utils/resultsTableUtils";

export default function MasterResultsPreview({
  periods,
  dailyRows,
  balanceUsd,
  loading,
  error,
  sourceNote,
  firstTradingDate,
  lastTradingDate,
}) {
  const [activePeriod, setActivePeriod] = useState("daily");
  const [tableMonthUtc, setTableMonthUtc] = useState(currentUtcMonth);

  const activeData = periods?.[activePeriod] || null;

  const tableMonthBounds = useMemo(
    () => tableMonthBoundsFromDaily(dailyRows, firstTradingDate),
    [dailyRows, firstTradingDate],
  );

  useEffect(() => {
    setTableMonthUtc((prev) => {
      const k = monthKeyUtc(prev);
      const minK = monthKeyUtc(tableMonthBounds.min);
      const maxK = monthKeyUtc(tableMonthBounds.max);
      const nk = Math.max(minK, Math.min(maxK, k));
      return utcMonthFromKey(nk);
    });
  }, [tableMonthBounds]);

  const shiftPerformanceTableMonth = useCallback(
    (delta) => {
      setTableMonthUtc((prev) => {
        const nk = monthKeyUtc(prev) + delta;
        const minK = monthKeyUtc(tableMonthBounds.min);
        const maxK = monthKeyUtc(tableMonthBounds.max);
        const k = Math.max(minK, Math.min(maxK, nk));
        return utcMonthFromKey(k);
      });
    },
    [tableMonthBounds],
  );

  const performanceTable = useMemo(
    () =>
      buildPerformanceTableRows(
        activePeriod,
        dailyRows,
        activePeriod === "daily" || activePeriod === "weekly"
          ? tableMonthUtc
          : undefined,
      ),
    [activePeriod, dailyRows, tableMonthUtc],
  );

  const tableMonthLabel = formatMonthYearUtcLabel(
    tableMonthUtc.y,
    tableMonthUtc.m0,
  );
  const atMinTableMonth =
    monthKeyUtc(tableMonthUtc) <= monthKeyUtc(tableMonthBounds.min);
  const atMaxTableMonth =
    monthKeyUtc(tableMonthUtc) >= monthKeyUtc(tableMonthBounds.max);
  const tableMonthIsCurrent =
    monthKeyUtc(tableMonthUtc) === monthKeyUtc(currentUtcMonth());

  const performanceSectionTitle =
    activePeriod === "daily"
      ? "Daily performance"
      : activePeriod === "weekly"
        ? "Weekly performance"
        : activePeriod === "monthly"
          ? "Monthly performance"
          : "Yearly performance";

  const firstColumnHeader =
    activePeriod === "daily"
      ? "Date"
      : activePeriod === "weekly"
        ? "Week"
        : activePeriod === "monthly"
          ? "Month"
          : "Year";

  return (
    <div className="rounded-2xl border border-white/10 bg-[#0B1437] p-4 text-white sm:p-6">
      <div className="mb-4 text-center">
        <h2 className="text-xl font-bold sm:text-2xl">
          Preview — same layout as /Results
        </h2>
        {sourceNote ? (
          <p className="mt-2 text-xs text-gray-400">{sourceNote}</p>
        ) : null}
        {firstTradingDate && lastTradingDate ? (
          <p className="mt-1 text-xs text-gray-500">
            History: {firstTradingDate} → {lastTradingDate}
          </p>
        ) : null}
        {balanceUsd != null ? (
          <p className="mt-1 text-sm text-gray-300">
            Master balance:{" "}
            <span className="font-semibold text-white">
              ${Number(balanceUsd).toLocaleString("en-US")}
            </span>
          </p>
        ) : null}
      </div>

      <section className="rounded-2xl border border-white/10 bg-white/[0.04] p-4 sm:p-6">
        <div className="mb-5 flex flex-wrap items-center justify-center gap-3">
          {periodOrder.map((period) => {
            const isActive = activePeriod === period;
            return (
              <button
                key={period}
                type="button"
                onClick={() => setActivePeriod(period)}
                className={`rounded-xl px-5 py-2 text-sm font-semibold uppercase tracking-[0.08em] transition ${
                  isActive
                    ? "bg-brand-500 text-white"
                    : "border border-white/25 bg-white/5 text-gray-200 hover:bg-white/10"
                }`}
              >
                {period.charAt(0).toUpperCase() + period.slice(1)}
              </button>
            );
          })}
        </div>

        <div className="mb-6 rounded-2xl border border-white/10 bg-[#0a102c]/70 p-4">
          {loading ? (
            <p className="text-sm text-gray-300">Loading performance data…</p>
          ) : error ? (
            <p className="text-sm text-rose-300">{error}</p>
          ) : (
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {[
                ["PnL %", formatValue(activeData?.pnlPercent, "%")],
                ["Win Rate", formatValue(activeData?.winRate, "%")],
                ["Closed Trades", formatValue(activeData?.trades)],
                ["Pips +", formatValue(activeData?.pipsPositive)],
                ["Pips −", formatValue(activeData?.pipsNegative)],
                ["Drawdown", formatValue(activeData?.drawdownPercent, "%")],
              ].map(([label, value]) => (
                <div
                  key={label}
                  className="rounded-xl border border-white/10 bg-white/[0.03] p-4"
                >
                  <p className="text-xs uppercase tracking-[0.12em] text-gray-400">
                    {label}
                  </p>
                  <p className="mt-1 text-xl font-bold text-white">{value}</p>
                </div>
              ))}
            </div>
          )}
          {!loading && !error && activeData?.periodLabel ? (
            <p className="mt-4 text-xs text-gray-400">{activeData.periodLabel}</p>
          ) : null}
        </div>
      </section>

      <section className="mt-4 rounded-2xl border border-white/10 bg-white/[0.04] p-4 sm:p-6">
        <h3 className="text-center text-xl font-bold">{performanceSectionTitle}</h3>
        {(activePeriod === "daily" || activePeriod === "weekly") && !loading ? (
          <div className="mt-5 flex flex-col items-center gap-3 sm:flex-row sm:flex-wrap sm:justify-center">
            <div className="flex flex-wrap items-center justify-center gap-2">
              <button
                type="button"
                onClick={() => shiftPerformanceTableMonth(-1)}
                disabled={atMinTableMonth}
                className={`rounded-xl px-4 py-2 text-sm font-semibold transition ${
                  atMinTableMonth
                    ? "cursor-not-allowed border border-white/10 bg-white/[0.02] text-gray-500"
                    : "border border-white/25 bg-white/5 text-white hover:bg-white/10"
                }`}
              >
                ← Previous month
              </button>
              <span className="min-w-[10rem] rounded-xl border border-white/15 bg-[#0a102c]/80 px-4 py-2 text-center text-sm font-semibold text-white">
                {tableMonthLabel}
              </span>
              <button
                type="button"
                onClick={() => shiftPerformanceTableMonth(1)}
                disabled={atMaxTableMonth}
                className={`rounded-xl px-4 py-2 text-sm font-semibold transition ${
                  atMaxTableMonth
                    ? "cursor-not-allowed border border-white/10 bg-white/[0.02] text-gray-500"
                    : "border border-white/25 bg-white/5 text-white hover:bg-white/10"
                }`}
              >
                Next month →
              </button>
            </div>
            {!tableMonthIsCurrent ? (
              <button
                type="button"
                onClick={() => setTableMonthUtc(currentUtcMonth())}
                className="rounded-xl bg-brand-500/90 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-500"
              >
                Jump to current month
              </button>
            ) : null}
          </div>
        ) : null}

        <div className="mt-4 overflow-x-auto rounded-xl border border-white/10">
          {loading ? (
            <p className="p-4 text-sm text-gray-400">Loading table…</p>
          ) : (performanceTable.mode === "monthly" ||
              performanceTable.mode === "yearly") &&
            performanceTable.rows.length === 0 ? (
            <p className="p-4 text-sm text-gray-400">
              No {performanceTable.mode} aggregates yet for the selected pairs.
            </p>
          ) : (
            <table className="w-full min-w-[640px] text-left text-sm text-gray-200">
              <thead className="border-b border-white/10 bg-[#0a102c]/80 text-xs uppercase tracking-wide text-gray-400">
                <tr>
                  <th className="px-3 py-3 font-semibold">{firstColumnHeader}</th>
                  <th className="px-3 py-3 font-semibold">Pips+</th>
                  <th className="px-3 py-3 font-semibold">Pips−</th>
                  <th className="px-3 py-3 font-semibold">Trades</th>
                  <th className="px-3 py-3 font-semibold">W/L</th>
                  <th className="px-3 py-3 font-semibold">Win rate</th>
                </tr>
              </thead>
              <tbody>
                {performanceTable.rows.map((row) => (
                  <tr
                    key={row.rowKey}
                    className="border-b border-white/5 hover:bg-white/[0.03]"
                  >
                    <td className="px-3 py-2.5 font-medium text-white">
                      {performanceTable.mode === "daily" && row.periodLabel
                        ? new Date(
                            row.periodLabel + "T12:00:00Z",
                          ).toLocaleDateString()
                        : row.periodLabel || "—"}
                    </td>
                    <td className="px-3 py-2.5">
                      {row.pips_positive != null
                        ? Number(row.pips_positive).toFixed(2)
                        : "—"}
                    </td>
                    <td className="px-3 py-2.5">
                      {row.pips_negative != null
                        ? Number(row.pips_negative).toFixed(2)
                        : "—"}
                    </td>
                    <td className="px-3 py-2.5">
                      {row.trades_count != null ? row.trades_count : "—"}
                    </td>
                    <td className="px-3 py-2.5">
                      {row.wins_count != null && row.losses_count != null
                        ? `${row.wins_count} / ${row.losses_count}`
                        : "—"}
                    </td>
                    <td className="px-3 py-2.5">
                      {row.win_rate_percent != null
                        ? `${Number(row.win_rate_percent).toFixed(1)}%`
                        : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </section>
    </div>
  );
}
